import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Label,
} from 'reactstrap';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  PaymentMethod,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../../../components/content/loader';
import ProcessingOverlay from '../../../../components/processing-overlay';
import styles from './card-component.module.scss';
import useResponsiveFontSize from '../../../../components/payments/stripe/useResponsiveFontSize';
import paymentAPI from '../../../deprecated/payment/api';
import { IAppState } from '../../../../store';
import { EVENTS, track, trackSignupFunnel } from 'src/util/track';
import { Coupon } from 'src/pages/deprecated/payment/types';
import { TagData } from 'src/store/signup-workflow/types';
import WalletComponent from './wallet-component';
import AfterPayComponent from './afterpay-component';
import ChallengeBundleAfterPayComponent from './challenge-bundle-afterpay-component';
import { Box } from '@mui/material';
import { useAppSelector } from 'src/store/hooks';
import colors from 'src/styles/equalution/colors';
import { setPaymentProcessing } from 'src/store/payment';
import { useDecision } from '@optimizely/react-sdk';
import { hideIntercomElement, isUserFromAustralia } from 'src/lib/utils';
import moment from 'moment';
import { PLANS } from 'src/pages/constants';
import { useChallengeProducts } from 'src/pages/subscription-new/hooks/useChallengeProducts';
import { saveShippingAddress } from 'src/services';
import { getMonthlyPricingDisplay } from 'src/pages/subscription-new/utils/plans.util';

interface OwnProps {
  selectedPlan: string;
  setPaymentMethod: (paymentMethod: PaymentMethod) => void;
  handleSubmit: () => void;
  paymentMethod: PaymentMethod | undefined;
  priceId: string;
  planName: string;
  finalPrice: number;
  coupon: Coupon | undefined;
  isChallengeAccepted: boolean;
  tagId: number;
  challengeObject: TagData | null;
  futureCommencement: boolean;
  productId: string;
  setErrorMessage: (error: string) => void;
  errorMessage: string;
  setWalletPaymentIntentId: (paypalIntent: string) => void;
  popupVisible: boolean;
  noIntentAvailable: boolean;
  setNoIntentAvailable: (noIntentAvailable: boolean) => void;
  isSubmitDisabled?: boolean;
  activeTab?: number;
  setActiveTab?: (tab: number) => void;
  isSupplementAddedProp?: boolean;
  isNutritionCoachingAdded?: boolean;
  claimGift?: boolean;
  isSistemaGiftActive: boolean;
  submitButtonText?: string;
  expiryLabel?: string;
}

const useOptions = (): any => {
  useResponsiveFontSize();
  const options = useMemo(
    () => ({
      style: {
        base: {
          fontSize: '16px',
          color: '#1E202E',
          fontFamily: 'Lexend, sans-serif',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#E8594D',
        },
      },
      showIcon: true,
    }),
    []
  );

  return options;
};

const CardComponent = ({
  selectedPlan,
  setPaymentMethod,
  handleSubmit,
  priceId,
  finalPrice,
  planName,
  coupon,
  paymentMethod,
  isChallengeAccepted,
  tagId,
  challengeObject,
  futureCommencement,
  productId,
  setErrorMessage,
  errorMessage,
  setWalletPaymentIntentId,
  popupVisible,
  noIntentAvailable,
  setNoIntentAvailable,
  isSubmitDisabled,
  activeTab = 0,
  setActiveTab,
  isSupplementAddedProp = false,
  isNutritionCoachingAdded = false,
  claimGift = false,
  isSistemaGiftActive,
  submitButtonText,
  expiryLabel,
}: OwnProps) => {
  // Redux Selectors
  const user = useSelector((state: IAppState) => state.auth?.user);
  const isPaymentProcessing = useSelector(
    (state: IAppState) => state.payment.isPaymentProcessing
  );

  const { challengeEndDate } = useChallengeProducts();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasSubmittedRef = useRef(false);
  const trackedPaymentMethodRef = useRef<number | null>(null);

  const dispatch = useDispatch();
  const {
    isFitherProjectUser,
    isChallengePackageFlow,
    isChallengeBundleFlow,
    shippingAddress,
    proteinFlavor,
    isSupplementAdded,
  } = useAppSelector(state => state.signUp);
  const cardErrorMessageTimeout = undefined;
  const cvcErrorMessageTimeout = undefined;
  const expiryErrorMessageTimeout = undefined;

  const [{ enabled: isAfterPayDisabled }] = useDecision(
    'disable_afterpay_all_flow',
    {
      autoUpdate: true,
    }
  );

  const shouldSaveShippingAddress =
    (isChallengePackageFlow &&
      (selectedPlan === PLANS.CHALLENGE_WITH_SUPPLEMENTS ||
        isSistemaGiftActive)) ||
    isSupplementAdded;

  const stripe = useStripe();
  const elements = useElements();
  const options = useOptions();

  const [stripeCustomer, setStripeCustomer] = useState<any>(null);
  const [hideWallet, toggleHideWallet] = useState(false);

  const [cardErrorMessage, setCardErrorMessage] = useState<undefined | string>(
    undefined
  );
  const [cvcErrorMessage, setCvcErrorMessage] = useState<undefined | string>(
    undefined
  );
  const [expiryErrorMessage, setExpiryErrorMessage] = useState<
    undefined | string
  >(undefined);

  const [hasCard, setHasCard] = useState<boolean>(false);
  const [hasExpiry, setHasExpiry] = useState<boolean>(false);
  const [hasCVC, setHasCVC] = useState<boolean>(false);

  const hasAnyAddon = isSupplementAddedProp || isNutritionCoachingAdded;

  const showAfterpayTab = useMemo(() => {
    if (isAfterPayDisabled) {
      return false;
    }

    const condition =
      !selectedPlan.includes('Weekly') &&
      isUserFromAustralia() && // only allow AUS users for AfterPay
      !noIntentAvailable &&
      !isFitherProjectUser &&
      (!isChallengePackageFlow || isChallengeBundleFlow) && // Hide Afterpay for challenge package (supplements) but allow for bundle
      !hasAnyAddon; // Hide Afterpay when nutrition coaching or supplements are added in normal signup

    return condition;
  }, [
    isAfterPayDisabled,
    isChallengePackageFlow,
    isChallengeBundleFlow,
    isFitherProjectUser,
    noIntentAvailable,
    selectedPlan,
    hasAnyAddon,
  ]);

  useEffect(() => {
    //create customer on load of the step
    if (user && !stripeCustomer) {
      const createCustomer = async () => {
        try {
          const { data } = await paymentAPI.post('stripe/customer/create', {
            email: user.email,
            userID: user.id,
          });
          setStripeCustomer(data.customer);
        } catch (error) {
          console.error('Failed to stripe create customer', error);
        }
      };
      createCustomer();
    }
  }, [user, stripeCustomer]);

  useEffect(() => {
    if (selectedPlan.includes('Weekly')) {
      setActiveTab?.(0);
    }
  }, [selectedPlan, setActiveTab]);

  // Auto-switch from Afterpay to Card tab when addon is added
  useEffect(() => {
    if (hasAnyAddon && activeTab === 1 && !isChallengePackageFlow) {
      setActiveTab?.(0); // Switch to card tab
    }
  }, [hasAnyAddon, activeTab, setActiveTab, isChallengePackageFlow]);

  useEffect(() => {
    if (user && paymentMethod && !errorMessage && !isSubmitting) {
      setIsSubmitting(true);
      handleSubmit();
      // Reset submitting state after handing off to parent - CardComponent's job is done
      setIsSubmitting(false);
      hasSubmittedRef.current = false;
    }
  }, [user, paymentMethod, errorMessage, handleSubmit, isSubmitting]);

  useEffect(() => {
    if (trackedPaymentMethodRef.current === activeTab) {
      return;
    }

    trackSignupFunnel(EVENTS.signupPaymentMethodSelected, {
      payment_method: activeTab === 1 ? 'afterpay' : 'card',
      selected_plan_id: priceId,
      is_challenge_package_flow: Boolean(isChallengePackageFlow),
    });
    trackedPaymentMethodRef.current = activeTab;
  }, [activeTab, isChallengePackageFlow, priceId]);

  useEffect(() => {
    toggleHideWallet(true); //this code is used to reload
    setTimeout(() => {
      toggleHideWallet(false);
    }, 1000);
  }, [coupon, selectedPlan]);

  const handleCardSubmit = async (
    event: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    setIsLoading(true);
    event.preventDefault();

    setErrorMessage('');
    dispatch(setPaymentProcessing(true));

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      return;
    }

    try {
      if (!user) {
        console.log('User not found');
        return;
      }

      if (shouldSaveShippingAddress && shippingAddress) {
        await saveShippingAddress(
          shippingAddress,
          proteinFlavor ?? undefined,
          isSistemaGiftActive
        );
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        console.log(error);
        trackSignupFunnel(EVENTS.signupPaymentFailed, {
          payment_method: 'card',
          failure_stage: 'payment_method_setup',
          error_message: error.message || 'Payment method creation failed',
          selected_plan_id: priceId,
        });
        setErrorMessage(error.message || 'Payment method creation failed');
        dispatch(setPaymentProcessing(false));
        setIsLoading(false);
        setIsSubmitting(false);
        hasSubmittedRef.current = false;
        return;
      }

      if (paymentMethod) {
        setPaymentMethod(paymentMethod);
        track(EVENTS.paymentAdded, {
          email: user.email,
          paymentMode: 'Card',
        });
      }
    } catch (e) {
      dispatch(setPaymentProcessing(false));
      setIsSubmitting(false);
      hasSubmittedRef.current = false;
      trackSignupFunnel(EVENTS.signupPaymentFailed, {
        payment_method: 'card',
        failure_stage: 'payment_method_setup',
        error_message:
          typeof e === 'string' ? e : e instanceof Error ? e.message : '',
        selected_plan_id: priceId,
      });
      if (typeof e === 'string') {
        setErrorMessage(e);
      } else if (e instanceof Error) {
        setErrorMessage(e.message);
      }
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const errorMessageTimeoutHandler = (
    timeoutVariable: unknown,
    event:
      | StripeCardCvcElementChangeEvent
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent,
    errorMessageSetFunction: any
  ): void => {
    if (timeoutVariable) {
      clearTimeout(timeoutVariable as NodeJS.Timeout);
    }

    setTimeout(() => {
      if (
        !event.complete ||
        (typeof event.error !== 'undefined' && event.error.message)
      ) {
        errorMessageSetFunction(event.error ? event.error.message : undefined);
      } else {
        errorMessageSetFunction(undefined);
      }
    }, 500);
  };

  //To hide intercom chat on payment page - to help mobile users.
  useEffect(() => {
    setTimeout(() => {
      hideIntercomElement();
    }, 1000);
  }, []);

  return (
    <div className={styles.cardContainer}>
      {stripeCustomer && !hideWallet && (
        <WalletComponent
          priceId={priceId}
          finalPrice={finalPrice}
          planName={planName}
          coupon={coupon}
          isChallengeAccepted={isChallengeAccepted}
          tagId={tagId}
          challengeObject={challengeObject}
          futureCommencement={futureCommencement}
          productId={productId}
          stripeCustomer={stripeCustomer}
          setWalletPaymentIntentId={setWalletPaymentIntentId}
          popupVisible={popupVisible}
          isNoIntentAvailable={noIntentAvailable}
          setIsNoIntentAvailable={setNoIntentAvailable}
          isSubmitDisabled={isSubmitDisabled}
          claimGift={claimGift}
          isSistemaGiftActive={isSistemaGiftActive}
          setErrorMessage={setErrorMessage}
        />
      )}

      <div className={styles.paymentMethodsContainer}>
        <div
          className={`${styles.cardTabs} ${
            activeTab === 0 && styles.activeTab
          }`}
          onClick={() => {
            if (isPaymentProcessing) {
              return;
            }
            setActiveTab?.(0);
          }}>
          <div className={`${styles.creditCardIcon}`} />
          <div className={styles.creditText}>CREDIT OR DEBIT CARD</div>
        </div>
        {showAfterpayTab && (
          <div
            className={`${styles.cardTabs} ${
              activeTab === 1 && styles.activeTab
            }`}
            onClick={() => {
              if (isPaymentProcessing) {
                return;
              }
              setActiveTab?.(1);
            }}>
            <div className={`${styles.afterPayIcon}`}></div>
          </div>
        )}
      </div>
      {activeTab === 0 && (
        <Form
          onSubmit={handleCardSubmit}
          style={{
            display: isPaymentProcessing || isLoading ? 'none' : 'block',
            textAlign: 'center',
          }}>
          <FormGroup className={cardErrorMessage ? 'is-invalid' : ''}>
            <div className="label-feedback">
              <Label>Card number</Label>
              <FormFeedback valid>Success</FormFeedback>
              <FormFeedback valid={false}>{cardErrorMessage}</FormFeedback>
            </div>
            <CardNumberElement
              options={options}
              className={`${styles.input} ${
                cardErrorMessage && styles.invalid
              }`}
              onChange={(event: StripeCardNumberElementChangeEvent): void => {
                errorMessageTimeoutHandler(
                  cardErrorMessageTimeout,
                  event,
                  (message: string) => {
                    if (event.complete) {
                      setHasCard(true);
                    } else {
                      setHasCard(false);
                      setCardErrorMessage(message);
                    }
                  }
                );
              }}
            />
          </FormGroup>
          <div className={styles.cardMetaDetails}>
            <div className={styles.cardExpiryDateContainer}>
              <FormGroup className={expiryErrorMessage ? 'is-invalid' : ''}>
                <div className="label-feedback">
                  <Label>{expiryLabel || 'Expiry date'}</Label>
                  <FormFeedback valid>Success</FormFeedback>
                  <FormFeedback valid={false}>
                    {expiryErrorMessage}
                  </FormFeedback>
                </div>
                <CardExpiryElement
                  options={options}
                  className={`${styles.input} ${
                    expiryErrorMessage && styles.invalid
                  }`}
                  onChange={(
                    event: StripeCardExpiryElementChangeEvent
                  ): void => {
                    errorMessageTimeoutHandler(
                      expiryErrorMessageTimeout,
                      event,
                      (message: string) => {
                        if (event.complete) {
                          setHasExpiry(true);
                        } else {
                          setHasExpiry(false);
                          setExpiryErrorMessage(message);
                        }
                      }
                    );
                  }}
                />
              </FormGroup>
            </div>
            <div className={styles.cardSecurityCodeContainer}>
              <FormGroup className={cvcErrorMessage ? 'is-invalid' : ''}>
                <div className="label-feedback">
                  <Label>Security code</Label>
                  <FormFeedback valid>Success</FormFeedback>
                  <FormFeedback valid={false}>{cvcErrorMessage}</FormFeedback>
                </div>
                <CardCvcElement
                  options={options}
                  className={`${styles.input} ${
                    cvcErrorMessage && styles.invalid
                  }`}
                  onChange={(event: StripeCardCvcElementChangeEvent): void => {
                    errorMessageTimeoutHandler(
                      cvcErrorMessageTimeout,
                      event,
                      (message: string) => {
                        if (event.complete) {
                          setHasCVC(true);
                        } else {
                          setHasCVC(false);
                          setCvcErrorMessage(message);
                        }
                      }
                    );
                  }}
                />
              </FormGroup>
            </div>
          </div>
          {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
          {isFitherProjectUser && (
            <Box fontSize={'12px'} fontWeight={400} color={colors.OFF_BLACK}>
              Auto-renewal billing. Cancel anytime.
            </Box>
          )}
          {isChallengePackageFlow && !isChallengeBundleFlow && (
            <Box
              textAlign={'left'}
              width={'100%'}
              fontSize={'12px'}
              fontWeight={400}>
              Your subscription will automatically renew at{' '}
              <span
                style={{
                  color: colors.SALAD,
                  fontWeight: 600,
                }}>
                {getMonthlyPricingDisplay()}/month
              </span>{' '}
              unless cancelled before{' '}
              <span
                style={{
                  fontWeight: 700,
                }}>
                {challengeEndDate
                  ? moment(challengeEndDate).format('MMMM D, YYYY')
                  : ''}
              </span>
            </Box>
          )}
          <Button
            type="submit"
            size="md"
            disabled={(() => {
              const disabledValues = {
                noStripe: !stripe,
                isLoading,
                isPaymentProcessing,
                isSubmitting,
                noCard: !hasCard,
                noCVC: !hasCVC,
                noExpiry: !hasExpiry,
                isSubmitDisabled,
              };
              return (
                disabledValues.noStripe ||
                disabledValues.isLoading ||
                disabledValues.isPaymentProcessing ||
                disabledValues.isSubmitting ||
                disabledValues.noCard ||
                disabledValues.noCVC ||
                disabledValues.noExpiry ||
                disabledValues.isSubmitDisabled
              );
            })()}
            className={styles.button}
            style={{
              ...(isFitherProjectUser && {
                borderRadius: '28px',
                height: '70px',
              }),
            }}>
            {isFitherProjectUser ? (
              <Box mt={'-8px'}>
                <Box fontSize={'20px'}>Finalise your program</Box>
                <Box fontSize={'14px'}>Redeem free month</Box>
              </Box>
            ) : (
              <Box>{submitButtonText || 'Finalise your program'}</Box>
            )}
          </Button>
        </Form>
      )}

      {activeTab === 1 &&
        (isChallengeBundleFlow ? (
          <ChallengeBundleAfterPayComponent
            priceId={priceId}
            finalPrice={finalPrice}
            coupon={coupon}
            isChallengeAccepted={isChallengeAccepted}
            tagId={tagId}
            challengeObject={challengeObject}
            futureCommencement={futureCommencement}
            productId={productId}
            stripeCustomer={stripeCustomer}
            isDisabled={isSubmitDisabled}
          />
        ) : (
          <AfterPayComponent
            priceId={priceId}
            finalPrice={finalPrice}
            planName={planName}
            coupon={coupon}
            isChallengeAccepted={isChallengeAccepted}
            tagId={tagId}
            challengeObject={challengeObject}
            futureCommencement={futureCommencement}
            productId={productId}
            stripeCustomer={stripeCustomer}
            setWalletPaymentIntentId={setWalletPaymentIntentId}
            isDisabled={isSubmitDisabled}
          />
        ))}

      {isPaymentProcessing || isLoading ? <Loader /> : <></>}
      {isPaymentProcessing && (
        <ProcessingOverlay message="Processing your payment..." />
      )}
    </div>
  );
};

export default CardComponent;
